import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, HandHeart, ChatCircleText, Feather, ShieldCheck, Quotes, Sparkle, Heart } from "@phosphor-icons/react";
import { api } from "../lib/api";

const HERO = "/api/media/reconnect-hero.png";
const EDIT_HANDS = "/api/media/reconnect-hands.png";

const CATEGORY_TILES = [
  { name: "Lingerie", to: "/shop?category=bras", image: "https://images.unsplash.com/photo-1610241519159-8a62634bac9a?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85", tag: "Everyday intimates" },
  { name: "Loungewear", to: "/shop?category=loungewear", image: "https://images.unsplash.com/photo-1766056278976-87f269e3b69c?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85", tag: "Slow mornings" },
  { name: "Nightwear", to: "/shop?category=nightwear", image: "https://images.unsplash.com/photo-1642945680515-faada4c0ca7b?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85", tag: "Something exotic" },
];

const PRINCIPLES = [
  { icon: HandHeart, title: "Touch, not performance", body: "For presence, not a show. Comfort over spectacle." },
  { icon: ChatCircleText, title: "Communication first", body: "Cards that make it easier to say what has quietly gone unsaid." },
  { icon: Feather, title: "Rooted in real technique", body: "Co-created with a licensed intimacy therapist. Warm, never clinical." },
  { icon: ShieldCheck, title: "Private and paced", body: "One evening. Repeatable whenever you need it. No timeline, no scripts." },
];

const KIT_CONTENTS = [
  { title: "One intimate piece", body: "Soft, minimal, made for presence — not performance." },
  { title: "10–15 ritual cards", body: "Alternating touch prompts and conversation prompts, rooted in sensate focus and vulnerability practice." },
  { title: "A guided audio session", body: "15–20 minutes, co-created with a licensed intimacy therapist. Play it together." },
  { title: "The 'why' guide", body: "A short printed guide — the science of touch and closeness, framed warmly." },
];

const ANGLES = [
  { icon: Sparkle, for: "For newlyweds", headline: "Start the way you want to continue.", body: "Build the intimacy habits most couples only reach for years later — before life gets loud." },
  { icon: Heart, for: "For long-married couples", headline: "Find your way back to each other.", body: "Quiet, non-judgemental. Not a fix. A re-entry point for couples who simply forgot how to be still together." },
];

const TESTIMONIALS = [
  { quote: "It gave us permission to slow down. We didn't realise how much we'd been rushing until we stopped.", attribution: "Married, 12 years" },
  { quote: "Twenty minutes, and it felt like a small room in the house we'd forgotten we owned.", attribution: "Together, 6 years" },
  { quote: "I gave it as a wedding gift and got a text three weeks later that said only, 'thank you.'", attribution: "Gifter" },
];

export default function Home() {
  const [ritual, setRitual] = useState(null);

  useEffect(() => {
    api.get("/products/reconnect-ritual").then(r => setRitual(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative w-full min-h-[calc(100vh-92px)] grid lg:grid-cols-12" data-testid="hero-section">
        <div className="lg:col-span-6 relative min-h-[50vh] lg:min-h-full overflow-hidden order-1" data-testid="hero-image-frame">
          <img src={HERO} alt="Reconnect" className="ken-burns absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/40 via-transparent to-transparent lg:hidden" />
          {/* Editorial "Made in-house" mark */}
          <div className="hidden lg:flex absolute top-6 left-6 items-center gap-2 bg-[#FAF8F5]/85 backdrop-blur-sm px-3 py-2" data-testid="made-in-house-tag">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4A1525] animate-pulse" />
            <span className="overline text-[9px] text-[#1A1A1A] tracking-[0.25em]">Made in-house · Editorial ·26</span>
          </div>
        </div>

        <div className="lg:col-span-6 bg-[#FAF8F5] px-6 lg:px-16 py-16 lg:py-20 flex flex-col justify-center order-2">
          <div className="max-w-xl fade-up">
            <div className="overline text-[#4A1525] mb-6">Introducing · Reconnect</div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-6">
              Not another<br/>lingerie box.<br/><em className="text-[#4A1525] font-light">A way back<br/>to each other.</em>
            </h1>
            <p className="text-[#5C5C5C] leading-relaxed mb-8 lg:text-lg">
              A guided intimacy experience for couples — designed with a licensed intimacy therapist — combining touch, conversation, and presence. No performance pressure. Just a structured evening to actually be with each other again.
            </p>

            {ritual && (
              <Link to={`/product/${ritual.slug}`} className="flex items-center gap-5 group py-4 border-y border-[#E6DED5] hover:pl-2 transition-all mb-8" data-testid={`hero-product-${ritual.slug}`}>
                <div className="w-16 h-20 flex-shrink-0 overflow-hidden bg-[#F0EBE1]">
                  <img src={ritual.images[0]} alt={ritual.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg text-[#1A1A1A] group-hover:text-[#4A1525] transition-colors">{ritual.name}</div>
                  <div className="overline text-[#5C5C5C] text-[10px] mt-1">Reconnect · The flagship</div>
                </div>
                <div className="text-sm text-[#1A1A1A] tabular-nums">₹{ritual.price.toFixed(0)}</div>
                <ArrowUpRight size={16} weight="light" className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            )}

            <div className="flex gap-3 flex-wrap">
              <Link to="/product/reconnect-ritual" className="bg-[#4A1525] text-[#FAF8F5] px-8 py-4 overline hover:bg-[#360F1B] transition-colors" data-testid="hero-shop-cta">Discover the Ritual</Link>
              <Link to="/reconnect" className="border border-[#1A1A1A] px-8 py-4 overline hover:bg-[#1A1A1A] hover:text-[#FAF8F5] transition-colors" data-testid="hero-lookbook-cta">The Story</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why this exists */}
      <section className="max-w-3xl mx-auto px-6 py-24 lg:py-32 text-center" data-testid="philosophy-section">
        <div className="overline text-[#4A1525] mb-6">Reconnection rituals for real intimacy</div>
        <p className="font-serif text-3xl md:text-4xl leading-tight text-[#1A1A1A] mb-8">
          Somewhere between the first spark and the comfortable routine, most couples lose a language for closeness.
        </p>
        <p className="text-[#5C5C5C] leading-relaxed text-lg max-w-2xl mx-auto">
          Not because love fades — because life gets loud. Reconnect is a small, structured way back.
        </p>
      </section>

      {/* Principles */}
      <section className="bg-[#F0EBE1] py-24" data-testid="principles-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="overline text-[#4A1525] mb-4">The principles</div>
            <h2 className="font-serif text-4xl md:text-6xl">A method, not a mood.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            {PRINCIPLES.map((p, i) => (
              <div key={i} className="text-center px-4 fade-up" style={{ animationDelay: `${i * 100}ms` }}>
                <p.icon size={40} weight="light" color="#4A1525" className="mx-auto mb-6" />
                <h3 className="font-serif text-2xl mb-4">{p.title}</h3>
                <p className="text-[#5C5C5C] leading-relaxed text-sm">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-[#E6DED5] overflow-hidden py-4 bg-[#FAF8F5]">
        <div className="marquee-track flex whitespace-nowrap gap-16 font-serif text-2xl italic text-[#4A1525]">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="flex gap-16 items-center">
              <span>Therapist-designed</span><span>·</span>
              <span>Made for two</span><span>·</span>
              <span>Discreet packaging</span><span>·</span>
              <span>One quiet evening</span><span>·</span>
            </div>
          ))}
        </div>
      </div>

      {/* What's inside */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="how-section">
        <div className="grid lg:grid-cols-12 gap-16 mb-4">
          <div className="lg:col-span-5">
            <div className="overline text-[#4A1525] mb-4">Inside the box</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-tight mb-8">Four objects.<br/>One quiet evening.</h2>
            <p className="text-[#5C5C5C] leading-relaxed text-lg">
              A single ritual, not a six-month programme. Everything you need for a slow, structured evening back into closeness — nothing you don't.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 gap-x-8 gap-y-8">
            {KIT_CONTENTS.map((s, i) => (
              <div key={i} className="fade-up flex gap-6" style={{ animationDelay: `${i * 100}ms` }}>
                <div className="font-serif text-4xl italic text-[#4A1525] w-16 flex-shrink-0">·0{i + 1}</div>
                <div>
                  <h3 className="font-serif text-2xl mb-2">{s.title}</h3>
                  <p className="text-[#5C5C5C] leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Two ways in */}
      <section className="bg-[#F0EBE1] py-24 lg:py-32" data-testid="angles-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <div className="overline text-[#4A1525] mb-4">Two ways in</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-tight">One Ritual.<br/>Two beginnings.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {ANGLES.map((a, i) => (
              <div key={i} className="bg-white p-10 lg:p-12 border border-[#E6DED5] fade-up" style={{ animationDelay: `${i * 100}ms` }}>
                <a.icon size={28} weight="light" color="#4A1525" className="mb-6" />
                <div className="overline text-[#4A1525] mb-4">{a.for}</div>
                <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-6">{a.headline}</h3>
                <p className="text-[#5C5C5C] leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Also from the atelier — secondary category showcase */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="categories-section">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-xl">
            <div className="overline text-[#4A1525] mb-4">Also from the atelier</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">Beyond the Ritual.</h2>
          </div>
          <p className="text-[#5C5C5C] leading-relaxed md:max-w-sm">
            Quiet intimates, slow loungewear, and a little something for the nights that ask for it — for the days between rituals.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {CATEGORY_TILES.map((c, i) => (
            <Link
              key={c.name}
              to={c.to}
              data-testid={`category-tile-${c.name.toLowerCase()}`}
              className="group relative block overflow-hidden aspect-[3/4] fade-up"
              style={{ animationDelay: `${i * 120}ms` }}
            >
              <img
                src={c.image}
                alt={c.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/70 via-[#1A1A1A]/10 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-8 lg:p-10 text-[#FAF8F5]">
                <div className="overline text-[#FAF8F5]/80 mb-3">{c.tag}</div>
                <div className="flex items-end justify-between gap-4">
                  <h3 className="font-serif text-4xl lg:text-5xl leading-none">{c.name}</h3>
                  <ArrowUpRight size={22} weight="light" className="transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1" />
                </div>
                <div className="mt-4 h-px w-12 bg-[#FAF8F5]/60 transition-all duration-500 group-hover:w-24" />
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-14">
          <p className="text-[#5C5C5C] text-sm italic max-w-md mx-auto">
            Reconnect remains our quiet centre. Everything else is what surrounds it.
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="testimonials-section">
        <div className="text-center mb-16">
          <div className="overline text-[#4A1525] mb-4">Words from the pilot</div>
          <h2 className="font-serif text-4xl md:text-5xl">A quiet chorus.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="border border-[#E6DED5] p-8 lg:p-10 bg-white flex flex-col fade-up" style={{ animationDelay: `${i * 100}ms` }} data-testid={`testimonial-${i}`}>
              <Quotes size={28} weight="light" color="#4A1525" className="mb-6" />
              <p className="font-serif italic text-xl leading-relaxed mb-6 flex-1">"{t.quote}"</p>
              <div className="overline text-[#5C5C5C]">{t.attribution}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-3xl mx-auto px-6 py-24 lg:py-32 text-center" data-testid="final-cta">
        <p className="font-serif italic text-3xl md:text-5xl leading-tight text-[#1A1A1A] mb-10">
          "Not another lingerie box. A way back to each other."
        </p>
        <div className="overline text-[#5C5C5C] mb-12">— Iamravishing</div>
        <Link to="/product/reconnect-ritual" className="inline-block bg-[#4A1525] text-[#FAF8F5] px-12 py-5 overline hover:bg-[#360F1B] transition-colors" data-testid="final-kit-cta">
          Begin the Ritual
        </Link>
      </section>
    </div>
  );
}
